import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BandCard from '../components/BandCard';
import { SearchIcon, UsersIcon, MusicIcon, MapPinIcon, GlobeIcon, PlusIcon, FolderIcon, ArrowLeftIcon, CheckIcon, CopyIcon, Edit, MoreVertical, LinkIcon, TrashIcon } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { getBandsForUser } from '../services/BandService';
import logger from '../services/LoggingService';
import { useUIState } from '../contexts/UIStateContext';
import { useBandActions } from '../contexts/ActionsContext';
import eventBus from '../utils/EventBus';

const BandsPage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(isAuthenticated ? 'my' : 'all');
  const [searchTerm, setSearchTerm] = useState("");
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({ 
    name: '', 
    description: '', 
    genre: '', 
    location: '', 
    website: '',
    visibility: 'public'
  });
  const [selectedBand, setSelectedBand] = useState(null);
  const [inviteForm, setInviteForm] = useState({ role: 'member', maxUses: 1, expiresInDays: 7 });
  const [generatedInvite, setGeneratedInvite] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [editingBand, setEditingBand] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    genre: '',
    location: '',
    website: '',
    visibility: 'public'
  });

  // Separate state for each tab
  const [myBands, setMyBands] = useState([]);
  const [friendsBands, setFriendsBands] = useState([]);
  const [allBands, setAllBands] = useState([]);

  // Fetch bands based on category
  const fetchBands = async (category = activeTab) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/bands?category=${category}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        const bandsData = result.data || [];
        
        // Update the appropriate state based on category
        switch (category) {
          case 'my':
            setMyBands(bandsData);
            break;
          case 'friends':
            setFriendsBands(bandsData);
            break;
          case 'all':
            setAllBands(bandsData);
            break;
          default:
            break;
        }
      } else {
        console.error('Failed to fetch bands');
      }
    } catch (error) {
      console.error('Error fetching bands:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update activeTab when authentication state changes
  useEffect(() => {
    if (isAuthenticated && activeTab === 'all') {
      // If user just logged in and we're on "all" tab, switch to "my"
      setActiveTab('my');
    } else if (!isAuthenticated && activeTab !== 'all') {
      // If user logged out, switch to "all" tab
      setActiveTab('all');
    }
  }, [isAuthenticated]);

  // Single effect to handle all data loading
  useEffect(() => {
    console.log('BandsPage effect triggered, isAuthenticated:', isAuthenticated, 'activeTab:', activeTab);
    
    if (!isAuthenticated) {
      // For unauthenticated users, always fetch all public bands
      console.log('Fetching all bands for unauthenticated user');
      fetchBands('all');
    } else {
      // For authenticated users, fetch based on active tab
      console.log('Fetching bands for authenticated user, activeTab:', activeTab);
      fetchBands(activeTab);
    }
  }, [isAuthenticated, activeTab]); // Only depend on these two values

  // Set up event listeners for bands actions
  useEffect(() => {
    const createBandListener = eventBus.on('bands:create', () => {
      setShowCreateForm(true);
    });

    return () => {
      createBandListener();
    };
  }, []);

  // Handle URL parameters for editing
  useEffect(() => {
    const editBandId = searchParams.get('edit');
    if (editBandId && myBands.length > 0) {
      // Find the band to edit in the current bands
      const bandToEdit = myBands.find(band => band._id === editBandId);
      if (bandToEdit) {
        startEditBand(bandToEdit);
        // Clear the URL parameter
        setSearchParams({});
      }
    }
  }, [searchParams, myBands, setSearchParams]);

  // Filter bands based on search term
  const filterBands = (bands) => {
    if (!searchTerm) return bands;
    return bands.filter(band => 
      band.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (band.genre && band.genre.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (band.location && band.location.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  // Get current bands based on active tab
  const getCurrentBands = () => {
    switch (activeTab) {
      case 'my':
        return filterBands(myBands);
      case 'friends':
        return filterBands(friendsBands);
      case 'all':
        return filterBands(allBands);
      default:
        return [];
    }
  };

  // Handle edit band
  const handleEditBand = async (e) => {
    e.preventDefault();
    
    if (!editForm.name.trim()) {
      alert('Band name is required');
      return;
    }

    try {
      const response = await fetch(`/api/bands/${editingBand._id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editForm.name.trim(),
          description: editForm.description.trim(),
          genre: editForm.genre.trim(),
          location: editForm.location.trim(),
          website: editForm.website.trim(),
          visibility: editForm.visibility
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setEditingBand(null);
        setEditForm({
          name: '',
          description: '',
          genre: '',
          location: '',
          website: '',
          visibility: 'public'
        });
        alert(`Band "${result.data.name}" updated successfully!`);
        
        // Refresh the current tab
        fetchBands(activeTab);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update band');
      }
    } catch (error) {
      console.error('Error updating band:', error);
      alert(`Error updating band: ${error.message}`);
    }
  };

  // Start editing a band
  const startEditBand = (band) => {
    setEditingBand(band);
    setEditForm({
      name: band.name || '',
      description: band.description || '',
      genre: band.genre || '',
      location: band.location || '',
      website: band.website || '',
      visibility: band.visibility || 'public'
    });
  };

  // Delete band with confirmation
  const handleDeleteBand = async (band) => {
    const confirmMessage = `Are you sure you want to delete the band "${band.name}"?

This will:
- Convert all band setlists to personal setlists
- Remove all members from the band
- Delete all pending invites
- Permanently delete the band

This action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await fetch(`/api/bands/${band._id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Band "${band.name}" deleted successfully!

Cleanup summary:
- ${result.cleanup_summary.setlists_converted} setlists converted to personal
- ${result.cleanup_summary.memberships_deleted} memberships removed
- ${result.cleanup_summary.invites_deleted} invites deleted`);
        
        // Refresh the current tab
        fetchBands(activeTab);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete band');
      }
    } catch (error) {
      console.error('Error deleting band:', error);
      alert(`Error deleting band: ${error.message}`);
    }
  };

  // Create new band
  const handleCreateBand = async (e) => {
    e.preventDefault();
    
    if (!createForm.name.trim()) {
      alert('Band name is required');
      return;
    }

    try {
      const response = await fetch('/api/bands', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: createForm.name.trim(),
          description: createForm.description.trim(),
          genre: createForm.genre.trim(),
          location: createForm.location.trim(),
          website: createForm.website.trim(),
          visibility: createForm.visibility
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setCreateForm({ 
          name: '', 
          description: '', 
          genre: '', 
          location: '', 
          website: '',
          visibility: 'public'
        });
        setShowCreateForm(false);
        alert(`Band "${result.data.name}" created successfully!`);
        
        // Refresh the current tab
        fetchBands(activeTab);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create band');
      }
    } catch (error) {
      console.error('Error creating band:', error);
      alert(`Error creating band: ${error.message}`);
    }
  };

  // Generate invite link
  const handleGenerateInvite = async (bandId) => {
    try {
      const response = await fetch(`/api/bands/${bandId}/invites`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(inviteForm),
      });

      if (response.ok) {
        const result = await response.json();
        setGeneratedInvite(result.data);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate invite');
      }
    } catch (error) {
      console.error('Error generating invite:', error);
      alert(`Error generating invite: ${error.message}`);
    }
  };

  // Copy invite link to clipboard
  const handleCopyInviteLink = async () => {
    if (!generatedInvite?.invite_url) return;

    try {
      await navigator.clipboard.writeText(generatedInvite.invite_url);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = generatedInvite.invite_url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getBandStats = (band) => {
    const stats = [];
    if (band.stats?.member_count) stats.push(`${band.stats.member_count} members`);
    if (band.stats?.sheet_count) stats.push(`${band.stats.sheet_count} sheets`);
    if (band.stats?.setlist_count) stats.push(`${band.stats.setlist_count} setlists`);
    return stats.join(' • ');
  };

  const canManageBand = (band) => {
    return activeTab === 'my' && band.user_role === 'admin';
  };

  // Handler for showing invite form inline
  const [showInviteFormForBand, setShowInviteFormForBand] = useState(null);

  const handleInviteBand = (band) => {
    setShowInviteFormForBand(band._id);
    setSelectedBand(band);
    setGeneratedInvite(null);
    setCopiedLink(false);
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-4">Bands</h1>
        
        {/* Local action buttons */}
        {isAuthenticated && (
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-1.5 transition-colors"
            >
              <PlusIcon className="w-3 h-3" />
              Create Band
            </button>
          </div>
        )}
        
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            type="text"
            placeholder="Search bands..."
            className="pl-10 w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Edit Band Form */}
      {editingBand && (
        <div className="mb-6 bg-card border border-border rounded-md p-6">
          <h2 className="text-xl font-semibold mb-4">Edit Band</h2>
          <form onSubmit={handleEditBand} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Band Name *
                </label>
                <Input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Enter band name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Genre
                </label>
                <Input
                  type="text"
                  value={editForm.genre}
                  onChange={(e) => setEditForm({ ...editForm, genre: e.target.value })}
                  placeholder="e.g. Rock, Jazz, Pop"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <Input
                  type="text"
                  value={editForm.location}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                  placeholder="City, Country"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website
                </label>
                <Input
                  type="url"
                  value={editForm.website}
                  onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                  placeholder="https://yourband.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Describe your band"
                rows="3"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Visibility
              </label>
              <select
                value={editForm.visibility}
                onChange={(e) => setEditForm({ ...editForm, visibility: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="public">Public - Anyone can discover this band</option>
                <option value="private">Private - Only members can see this band</option>
              </select>
            </div>
            <div className="flex space-x-3">
              <Button type="submit">
                Update Band
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditingBand(null);
                  setEditForm({ 
                    name: '', 
                    description: '', 
                    genre: '', 
                    location: '', 
                    website: '',
                    visibility: 'public'
                  });
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Create Band Form */}
      {showCreateForm && isAuthenticated && (
        <div className="mb-6 bg-card border border-border rounded-md p-6">
          <h2 className="text-xl font-semibold mb-4">Create New Band</h2>
          <form onSubmit={handleCreateBand} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Band Name *
                </label>
                <Input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="Enter band name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Genre
                </label>
                <Input
                  type="text"
                  value={createForm.genre}
                  onChange={(e) => setCreateForm({ ...createForm, genre: e.target.value })}
                  placeholder="e.g. Rock, Jazz, Pop"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <Input
                  type="text"
                  value={createForm.location}
                  onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
                  placeholder="City, Country"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website
                </label>
                <Input
                  type="url"
                  value={createForm.website}
                  onChange={(e) => setCreateForm({ ...createForm, website: e.target.value })}
                  placeholder="https://yourband.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Describe your band"
                rows="3"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Visibility
              </label>
              <select
                value={createForm.visibility}
                onChange={(e) => setCreateForm({ ...createForm, visibility: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="public">Public - Anyone can discover this band</option>
                <option value="private">Private - Only members can see this band</option>
              </select>
            </div>
            <div className="flex space-x-3">
              <Button type="submit">
                Create Band
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateForm(false);
                  setCreateForm({ 
                    name: '', 
                    description: '', 
                    genre: '', 
                    location: '', 
                    website: '',
                    visibility: 'public'
                  });
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}
      
      {loading ? (
        <div className="text-center py-20">
          <p className="text-muted-foreground">Loading bands...</p>
        </div>
      ) : !isAuthenticated ? (
        // Show all public bands directly for unauthenticated users
        <div>
          {allBands.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-border rounded-md bg-background">
              <p className="text-muted-foreground">No public bands found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filterBands(allBands).map(band => (
                <BandCard 
                  key={band._id} 
                  band={band} 
                  canManage={false}
                  context="bands" 
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        // Show tabs for authenticated users
        <Tabs defaultValue="my" value={activeTab} className="w-full" onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="my">My Bands</TabsTrigger>
            <TabsTrigger value="friends">Friends Bands</TabsTrigger>
            <TabsTrigger value="all">All Bands</TabsTrigger>
          </TabsList>
          
          <TabsContent value="my">
            {getCurrentBands().length === 0 ? (
              <div className="text-center py-8 border border-dashed border-border rounded-md bg-background">
                <p className="text-muted-foreground">You don't have any bands yet</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => setShowCreateForm(true)}
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Create Band
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getCurrentBands().map(band => (
                  <BandCard 
                    key={band._id} 
                    band={band} 
                    canManage={canManageBand(band)}
                    onEdit={startEditBand}
                    onDelete={handleDeleteBand}
                    onInvite={handleInviteBand}
                    context="bands" 
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="friends">
            {getCurrentBands().length === 0 ? (
              <div className="text-center py-8 border border-dashed border-border rounded-md bg-background">
                <p className="text-muted-foreground">No friend bands found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getCurrentBands().map(band => (
                  <BandCard 
                    key={band._id} 
                    band={band} 
                    canManage={false}
                    context="bands" 
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="all">
            {getCurrentBands().length === 0 ? (
              <div className="text-center py-8 border border-dashed border-border rounded-md bg-background">
                <p className="text-muted-foreground">No public bands found</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => setShowCreateForm(true)}
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Be the first to create a band
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getCurrentBands().map(band => (
                  <BandCard 
                  key={band._id} 
                  band={band} 
                  canManage={false}
                  context="bands" 
                />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Invite Members Modal */}
      {showInviteFormForBand && selectedBand && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Invite Members to {selectedBand?.name}</h3>
              <button
                onClick={() => {
                  setShowInviteFormForBand(null);
                  setSelectedBand(null);
                  setGeneratedInvite(null);
                  setCopiedLink(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Generate an invite link to add new members to your band:
              </p>
              
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={inviteForm.maxUses}
                    onChange={(e) => setInviteForm({ ...inviteForm, maxUses: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Days</label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={inviteForm.expiresInDays}
                    onChange={(e) => setInviteForm({ ...inviteForm, expiresInDays: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <button
                onClick={() => handleGenerateInvite(selectedBand._id)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors mb-4"
              >
                Generate Invite Link
              </button>

              {/* Generated Invite */}
              {generatedInvite && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <h5 className="font-medium text-green-800 mb-2 text-sm">Invite Link Generated!</h5>
                  <p className="text-sm text-green-700 mb-3">
                    Role: {generatedInvite.role} | Expires: {formatDate(generatedInvite.expires_at)}
                  </p>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={generatedInvite.invite_url}
                      readOnly
                      className="flex-1 px-3 py-2 bg-white border border-green-300 rounded-md text-sm"
                    />
                    <button
                      onClick={handleCopyInviteLink}
                      className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        copiedLink 
                          ? 'bg-green-600 text-white' 
                          : 'bg-green-100 hover:bg-green-200 text-green-700'
                      }`}
                    >
                      {copiedLink ? (
                        <>
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowInviteFormForBand(null);
                  setSelectedBand(null);
                  setGeneratedInvite(null);
                  setCopiedLink(false);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BandsPage;